import React, { useState, useMemo } from 'react';
import ReinnoireCard from '../Shared/ReinnoireCard';
import DateField from '../Shared/DateField';
import ProceduraFileSection from '../Shared/ProceduraFileSection';
import { FilterPill } from '../Shared/FilterPill';
import './Tracker.css';

/* ── Tarife BOIP default (EUR, 2024) ── */
const TARIFE_DEFAULT = {
  standard: 35,
  plicSigilat: 70,
};

const TIP_CREATIE_OPTIONS = [
  ['concept','Concept'], ['design','Design'], ['text','Text'],
  ['software','Software'], ['muzica','Muzica'], ['altele','Altele'],
];

/* ── Helpers ── */
const todayMid = () => { const d = new Date(); d.setHours(0,0,0,0); return d; };
const parseISO = (s) => (s ? new Date(s + 'T00:00:00') : null);
const addYears = (d, y) => { const x = new Date(d); x.setFullYear(x.getFullYear() + y); return x; };
const fmtRO = (d) => d ? d.toLocaleDateString('ro-RO', { day:'2-digit', month:'2-digit', year:'numeric' }) : '—';
const daysLeft = (d) => d ? Math.ceil((d - todayMid()) / 86400000) : null;
const fmtEur = (n) => Number(n).toLocaleString('ro-RO') + ' €';

/* ── emptyRecord export ── */
export function emptyRecord() {
  return {
    denumire: '',
    tipCreatie: 'design',
    tipDepunere: 'standard',
    depusa: false,
    numar: '',
    dataDepunere: '',
    dataConfirmare: '',
    dataInregistrare: '',
    dataExpirare: '',
    reinnoire: false,
    certificatPrimit: false,
    transmisClient: false,
    observatii: '',
  };
}

/* ── Fee calculation ── */
function calcTaxe(r, T) {
  const total = r.tipDepunere === 'plic_sigilat' ? T.plicSigilat : T.standard;
  return { total };
}

/* ── Status / timeline ── */
function deriveStatus(r) {
  const expirare = parseISO(r.dataDepunere) ? addYears(parseISO(r.dataDepunere), 5) : null;
  const registered = !!(r.dataInregistrare);

  const steps = [
    { key:'depunere',    label:'Depunere I-Depot',        done: registered || !!r.depusa,          date: parseISO(r.dataDepunere) },
    { key:'confirmare',  label:'Confirmare depozit BOIP', done: registered || !!r.dataConfirmare,  date: parseISO(r.dataConfirmare) },
    { key:'certificat',  label:'Certificat primit',       done: r.certificatPrimit,   date: null },
    { key:'client',      label:'Transmis client',         done: r.transmisClient,     date: null },
  ];

  let alert;
  if (registered) {
    alert = (r.certificatPrimit && !r.transmisClient)
      ? { label:'De transmis certificatul catre client', sev:'act' }
      : { label:'I-Depot inregistrat', sev:'done' };
  } else if (!r.depusa) {
    alert = { label:'In pregatire — de depus', sev:'neutral' };
  } else if (!r.dataConfirmare) {
    alert = { label:'Asteptare confirmare depozit BOIP', sev:'info' };
  } else if (!r.certificatPrimit) {
    alert = { label:'De obtinut certificatul de depozit', sev:'act' };
  } else if (!r.transmisClient) {
    alert = { label:'De transmis certificatul catre client', sev:'act' };
  } else {
    alert = { label:'Procedura finalizata', sev:'done' };
  }

  if (alert.date) {
    const dl = daysLeft(alert.date);
    alert.daysLeft = dl;
    if (dl < 0) alert.sev = 'overdue';
  }

  if (expirare && (alert.sev === 'done' || alert.sev === 'act')) {
    const dl = daysLeft(expirare);
    if (dl <= 180) {
      alert = { label: dl < 0 ? 'I-Depot expirat — reinnoire necesara' : 'I-Depot expira in curand — reinnoire necesara', date: expirare, daysLeft: dl, sev: dl < 0 ? 'overdue' : 'watch' };
    }
  }

  return { steps, alert, expirare };
}

/* ════════════════════════ Component ════════════════════════ */
export default function IDepotTracker({ data, onChange, procId, showToast, pendingFiles, onPendingFilesChange }) {
  const [r, setR] = useState(data);
  const [tarife, setTarife] = useState(TARIFE_DEFAULT);
  const [showTarife, setShowTarife] = useState(false);

  const set = (patch) => {
    const next = { ...r, ...patch };
    setR(next);
    onChange(next);
  };

  const taxe = useMemo(() => calcTaxe(r, tarife), [r, tarife]);
  const status = useMemo(() => deriveStatus(r), [r]);

  return (
    <div className="tracker">
      <AlertBanner status={status} />
      <Timeline steps={status.steps} />

      {/* Identificare */}
      <TrackerCard title="Identificarea creatiei">
        <div className="f-row">
          <div className="field">
            <label>Denumire creatie</label>
            <input type="text" value={r.denumire} onChange={e => set({ denumire: e.target.value })} placeholder="ex: Colectie logo Aurora Style" />
          </div>
          <div className="field">
            <label>Tip creatie</label>
            <FilterPill value={r.tipCreatie} onChange={e => set({ tipCreatie: e.target.value })}>
              {TIP_CREATIE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </FilterPill>
          </div>
        </div>
        <div className="field">
          <label>Fisiere depozitate <span className="f-hint">— continutul exact depus la BOIP</span></label>
          <ProceduraFileSection procId={procId} showToast={showToast} pendingFiles={pendingFiles} onPendingFilesChange={onPendingFilesChange} />
        </div>
        <div className="field">
          <label>Tip depunere</label>
          <SegControl value={r.tipDepunere} onChange={v => set({ tipDepunere: v })} options={[['standard','Standard'],['plic_sigilat','Plic sigilat']]} />
        </div>
      </TrackerCard>

      {/* Calcul taxe */}
      <TrackerCard title="Calcul taxa BOIP" badge="tarife 2024">
        <table className="fee-table">
          <tbody>
            <FeeRow label={`Depunere I-Depot (${r.tipDepunere === 'plic_sigilat' ? 'plic sigilat' : 'standard'})`} value={taxe.total} />
          </tbody>
          <tfoot>
            <tr className="fee-total">
              <td>Total taxa BOIP</td>
              <td>{fmtEur(taxe.total)}</td>
            </tr>
          </tfoot>
        </table>

        <button type="button" className="btn-link mt8" onClick={() => setShowTarife(s => !s)}>
          {showTarife ? '▾' : '▸'} Tarife oficiale (editabile)
        </button>
        {showTarife && <TarifeEditor tarife={tarife} setTarife={setTarife} />}
      </TrackerCard>

      {/* Depunere + monitorizare */}
      <TrackerCard title="Depunere si monitorizare">
        <label className="check-label">
          <input type="checkbox" checked={r.depusa} onChange={e => set({ depusa: e.target.checked })} />
          <strong>I-Depot a fost depus</strong>
        </label>
        <p className="f-hint mb8" style={{ marginLeft: 22 }}>Nu exista examinare sau opozitie — doar confirmarea depozitului.</p>

        {r.depusa && (
          <div className="dep-section">
            <div className="f-row">
              <div className="field">
                <label>Nr. depozit BOIP</label>
                <input type="text" value={r.numar} onChange={e => set({ numar: e.target.value })} placeholder="ex. i-XXXXXX" className="mono" />
              </div>
              <div className="field">
                <label>Data depunerii</label>
                <DateField value={r.dataDepunere} onChange={v => set({ dataDepunere: v })} />
              </div>
            </div>

            <div className="field">
              <label>Data confirmarii depozitului</label>
              <DateField value={r.dataConfirmare} onChange={v => set({ dataConfirmare: v })} />
            </div>
            <div className="field">
              <label>Data inregistrarii</label>
              <DateField value={r.dataInregistrare || ''} onChange={v => set({ dataInregistrare: v })} />
            </div>
            <div className="field">
              <label>Data expirarii</label>
              <DateField value={r.dataExpirare || ''} onChange={v => set({ dataExpirare: v })} />
            </div>

            {status.expirare && (
              <div className="f-row">
                <DeadlineChip label="Expirare I-Depot (5 ani)" date={status.expirare} act />
              </div>
            )}
            <p className="f-hint">Reinnoibil din 5 in 5 ani, pana la maxim 50 de ani.</p>

            <div className="tr-divider">Finalizare</div>

            <label className="check-label">
              <input type="checkbox" checked={r.certificatPrimit} onChange={e => set({ certificatPrimit: e.target.checked })} />
              Certificatul de depozit a fost primit
            </label>
            <label className={`check-label${!r.certificatPrimit ? ' disabled' : ''}`}>
              <input type="checkbox" disabled={!r.certificatPrimit} checked={r.transmisClient} onChange={e => set({ transmisClient: e.target.checked })} />
              Certificatul a fost transmis catre client
            </label>
            <label className="check-label">
              <input type="checkbox" checked={r.reinnoire} onChange={e => set({ reinnoire: e.target.checked })} />
              Reinnoire depusa (ciclu urmator)
            </label>
          </div>
        )}
      </TrackerCard>

            {/* ── Reinnoire ── */}
      <ReinnoireCard r={r} set={set} TrackerCard={TrackerCard} />

      {/* Observatii */}
      <TrackerCard title="Observatii interne">
        <textarea className="tr-textarea" value={r.observatii} onChange={e => set({ observatii: e.target.value })} rows={3} placeholder="Note de dosar…" />
      </TrackerCard>

      {/* Documente inregistrare */}
      <TrackerCard title="Documente inregistrare">
        <ProceduraFileSection
          procId={procId}
          showToast={showToast}
          pendingFiles={pendingFiles}
          onPendingFilesChange={onPendingFilesChange}
          tipOptions={['Duplicat Cerere inregistrare', 'Copie Certificat Inregistrare']}
          keyPrefix="inreg"
        />
      </TrackerCard>
    </div>
  );
}

/* ── Sub-components ── */
function AlertBanner({ status }) {
  const { alert } = status;
  return (
    <div className={`tr-alert sev-${alert.sev}`}>
      <strong>{alert.label}</strong>
      {alert.date && (
        <span className="tr-alert-date">
          Termen: {fmtRO(alert.date)}
          {typeof alert.daysLeft === 'number' && (
            alert.daysLeft < 0
              ? ` · depasit cu ${Math.abs(alert.daysLeft)} zile`
              : ` · ${alert.daysLeft} zile ramase`
          )}
        </span>
      )}
    </div>
  );
}

function Timeline({ steps }) {
  const activeIdx = steps.findIndex(s => !s.done);
  return (
    <div className="tr-timeline">
      {steps.map((s, i) => {
        const isActive = i === activeIdx;
        const done = s.done;
        return (
          <React.Fragment key={s.key}>
            {i > 0 && <span className={`tl-line${done || steps[i-1].done ? ' tl-line-done' : ''}`} />}
            <div className="tl-step">
              <span className={`tl-dot${done ? ' done' : isActive ? ' active' : ''}`}>
                {done ? '✓' : i + 1}
              </span>
              <span className={`tl-label${isActive ? ' active' : done ? ' done' : ''}`}>{s.label}</span>
              {s.date && <span className="tl-date">{fmtRO(s.date)}</span>}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function TrackerCard({ title, badge, children, collapsible = false, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  const isOpen = !collapsible || open;
  return (
    <div className="tr-card">
      <div
        className={`tr-card-head${collapsible ? ' tr-card-head-toggle' : ''}`}
        onClick={collapsible ? () => setOpen(o => !o) : undefined}
      >
        <span className="tr-card-title">{title}</span>
        <div className="tr-card-head-right">
          {badge && <span className="tr-badge">{badge}</span>}
          {collapsible && <span className={`tr-card-arrow${isOpen ? ' tr-card-arrow-open' : ''}`}>›</span>}
        </div>
      </div>
      {isOpen && <div className="tr-card-body">{children}</div>}
    </div>
  );
}

function SegControl({ value, onChange, options, disabled }) {
  return (
    <div className={`seg-ctrl${disabled ? ' seg-disabled' : ''}`}>
      {options.map(([v, label]) => (
        <button key={v} type="button" disabled={disabled}
          className={`seg-btn${value === v ? ' seg-active' : ''}`}
          onClick={() => !disabled && onChange(v)}>
          {label}
        </button>
      ))}
    </div>
  );
}

function FeeRow({ label, value, strong, muted }) {
  return (
    <tr className={strong ? 'fee-strong' : muted ? 'fee-muted' : ''}>
      <td>{label}</td>
      <td className="fee-val">{fmtEur(value)}</td>
    </tr>
  );
}

function DeadlineChip({ label, date, act }) {
  const dl = daysLeft(date);
  const overdue = dl < 0;
  const soon = dl >= 0 && dl <= 14;
  const cls = overdue ? 'chip-overdue' : (act && soon) ? 'chip-act' : 'chip-neutral';
  return (
    <div className={`deadline-chip ${cls}`}>
      <span>{label}</span>
      <span>{fmtRO(date)} · {overdue ? `${Math.abs(dl)}z depasit` : `${dl}z`}</span>
    </div>
  );
}

function TarifeEditor({ tarife, setTarife }) {
  const fields = [
    ['standard','Standard'], ['plicSigilat','Plic sigilat'],
  ];
  return (
    <div className="tarife-editor">
      {fields.map(([k, lbl]) => (
        <label key={k} className="tarife-field">
          {lbl}
          <input type="number" value={tarife[k]}
            onChange={e => setTarife({ ...tarife, [k]: Number(e.target.value) || 0 })} />
        </label>
      ))}
    </div>
  );
}
